import { useTranslation } from 'react-i18next'
import { Button, Checkbox, TextField, InputAdornment, IconButton } from '@mui/material'

import PlusIcon from '../../assets/icons/plus-icon.svg'
import TrashIcon from '../../assets/icons/trash-icon.svg'
import FilterIcon from '../../assets/icons/filter-icon.svg'
import SearchIcon from '../../assets/icons/search-icon.svg'
import { Customer, FormError, SystemUser } from '../../types'
import { baseButtonTextWhiteBg, textFieldWhiteBg, baseButtonWarning } from '../../theme/custom-styles'

const ActionsBar = ({
  errors,
  usersData,
  className,
  userToSearch,
  userTypeName,
  hideFilterButton,
  showDeleteButton,
  processingDocument,
  generalActionChecked,
  handleSetUserToSearch,
  handleDialogOpenState,
  disableActionOfButtons,
  handleClearSearchInput,
  handleOpenFiltersOptionsModal,
  handleToggleGeneralActionChecked
}: {
  errors: FormError
  className?: string
  userTypeName: string
  userToSearch: string
  hideFilterButton?: boolean
  showDeleteButton: boolean
  processingDocument?: boolean
  generalActionChecked: boolean
  disableActionOfButtons?: boolean
  handleClearSearchInput: () => void
  usersData: Array<Customer | SystemUser>
  handleOpenFiltersOptionsModal?: () => void
  handleDialogOpenState: (actionToDo: string) => void
  handleToggleGeneralActionChecked: (checked: boolean) => void
  handleSetUserToSearch: (userToSearchData: string, showError: boolean) => void
}) => {
  const { t } = useTranslation(['common', 'customer-details-management'])

  return (
    <div className={className ? `users-management-actions-bar-container ${className}` : 'users-management-actions-bar-container'}>
      <div className="users-management-actions-bar-left">
        <div className="users-management-actions-bar-check-option">
          {processingDocument === false && (
            <Checkbox
              checked={usersData.length === 0 ? false : generalActionChecked === true ? true : false}
              disabled={usersData.length === 0 || disableActionOfButtons}
              onChange={(event) => handleToggleGeneralActionChecked(event.target.checked)}
            />
          )}
          {usersData.length === 0 ? (
            <div className="users-management-actions-bar-check-option-text">
              {t('usersActionBar.emptyListMessage', { userTypeName: userTypeName.endsWith('y') ? `${userTypeName.slice(0, -1)}ies` : `${userTypeName}s` })}
            </div>
          ) : (
            <div
              className="users-management-actions-bar-check-option-text"
              onClick={() => {
                if (processingDocument === false) {
                  const checkedStateUpdated = generalActionChecked === true ? false : true
                  handleToggleGeneralActionChecked(checkedStateUpdated)
                }
              }}
            >
              {`${t('usersActionBar.last')} ${usersData.length === 1 ? userTypeName : userTypeName.slice(-1) === 'y' ? `${userTypeName.slice(0, -1)}ies` : `${userTypeName}s`} `}
            </div>
          )}
        </div>
        <div className="users-management-actions-bar-button-action-container">
          {usersData.length > 0 && showDeleteButton && processingDocument === false ? (
            <Button
              fullWidth
              variant="text"
              sx={baseButtonWarning}
              startIcon={
                <img
                  src={TrashIcon}
                  alt="trash-icon"
                />
              }
              disabled={disableActionOfButtons}
              className="users-management-actions-bar-button-action"
              onClick={() => {
                handleDialogOpenState('delete')
              }}
            >
              <div className="users-management-actions-bar-button-action-text">
                {t('usersActionBar.buttonsTexts.deleteUser', { userTypeName: usersData.length === 1 ? userTypeName : userTypeName.slice(-1) === 'y' ? `${userTypeName.slice(0, -1)}ies` : `${userTypeName}s` })}
              </div>
            </Button>
          ) : (
            <Button
              fullWidth
              variant="text"
              sx={baseButtonTextWhiteBg}
              startIcon={
                <img
                  src={PlusIcon}
                  alt="plus-icon"
                />
              }
              disabled={disableActionOfButtons}
              className="users-management-actions-bar-button-action"
              onClick={() => {
                handleSetUserToSearch('', false)
                handleDialogOpenState('add')
              }}
            >
              <div className="users-management-actions-bar-button-action-text">{t('usersActionBar.buttonsTexts.addUser', { userTypeName })}</div>
            </Button>
          )}
          {!hideFilterButton && (
            <div
              style={{ cursor: 'pointer' }}
              className="filter-button-container"
              onClick={() => handleOpenFiltersOptionsModal?.()}
            >
              {/* !fromDateToSearch && !toDateToSearch && !beneficiaryToSearch ? (
                    <> */}
              <img
                src={FilterIcon}
                alt="filter-icon"
              />
              <div className="filter-button-text">{t('actionsBar.filter', { ns: 'customer-details-management' })}</div>
              {/* </>
                  ) : (
                    <>
                      <div className="filter-button-text">
                        {beneficiaryToSearch && !fromDateToSearch && !toDateToSearch
                          ? beneficiaryToSearch.split('-')[1]
                          : (fromDateToSearch || toDateToSearch) && !beneficiaryToSearch
                          ? `${format(addHours(new Date(fromDateToSearch as Date), 5), 'dd/MM/yyyy')} - ${format(addHours(new Date(toDateToSearch as Date), 5), 'dd/MM/yyyy')}`
                          : ''}
                      </div>
                      <div
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        onClick={() => {
                          setToDateToSearch(null)
                          setFromDateToSearch(null)
                          setBeneficiaryToSearch('')
                          setOpenPaymentFiltersModal(false)
                          setCustomerCompanyPaymentToSearch('')
                          handleSetCustomerCompanyPaymentToSearch('', false)
                        }}
                      >
                        <img
                          src={CancelIcon}
                          alt="cancel-icon"
                        />
                      </div>
                    </>
                  ) */}
            </div>
          )}
        </div>
      </div>
      <div className="users-management-actions-bar-right">
        <TextField
          variant="standard"
          sx={textFieldWhiteBg}
          label={t('usersActionBar.searchFieldText', { userTypeName })}
          value={userToSearch}
          error={!!errors.userToSearch}
          helperText={errors.userToSearch}
          disabled={disableActionOfButtons}
          className="users-management-actions-bar-search-input"
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              handleSetUserToSearch(userToSearch, true)
            }
          }}
          onChange={(event) => {
            handleSetUserToSearch(event.target.value, true)
          }}
          onBlur={() => {
            if (userToSearch === '') handleClearSearchInput()
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  aria-label="toggle password visibility"
                  sx={{ color: '#107569', right: '10px', bottom: '3px' }}
                >
                  <img
                    src={SearchIcon}
                    alt="search-icon"
                  />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </div>
    </div>
  )
}

export default ActionsBar
